import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { NgxSpinnerService } from 'ngx-spinner';
import { DynamicTableColumnsService } from 'src/app/_services/dynamic-table-column.service';
import { ErrorUtil } from 'src/app/_utilities/error';
import { PermissionService } from 'src/app/auth/permission.service';
import { Router } from '@angular/router';
import { AuthenticationService } from 'src/app/auth/authentication.service';
import { SubscriptionUtil } from 'src/app/_utilities/subscription';
import { LanguageTranslateService } from 'src/app/_services/language-translate.service';

@Component({
  selector: 'app-user-list-view',
  templateUrl: './user-list-view.component.html',
  standalone: false,
  // ✅ ADDED: OnPush stops Angular from running change detection on every
  //           browser event — breaks the infinite loop caused by p-table
  //           re-rendering on every change detection cycle
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserListViewComponent extends SubscriptionUtil implements OnInit, OnDestroy {

  default!: any[];
  edit: boolean = false;
  confirmYes: boolean = false;

  @Input() profileId: any;
  @Input() profile: any;

  profileTableColumnData: any;
  selectedData: any;
  menuType: any;
  module!: boolean;

  salePer!: boolean;
  servicePer!: boolean;
  fleetPer!: boolean;
  translate: any;
  tenantPackagesContract: any;
  tenantPackagesSites: any;
  // ✅ FIXED: addonFeature initialized once in ngOnInit, not inside chkChange()
  //           Previously called this.auth.getAddonFeatureLists() on every element
  //           returning a new object each time → p-table detects change → infinite loop
  addonFeature: any;

  constructor(
    private dynamicTableColumnsService: DynamicTableColumnsService,
    private spinner: NgxSpinnerService,
    private errorUtil: ErrorUtil,
    private perm: PermissionService,
    private auth: AuthenticationService,
    private router: Router,
    private languageTranslateService: LanguageTranslateService,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  ngOnInit(): void {
    this.tenantPackagesContract = this.auth.getPackageCodeWithList('CONTRACT');
    this.tenantPackagesSites = this.auth.getPackageCodeWithList('SITE');
    // ✅ FIXED: initialize addonFeature ONCE here instead of inside chkChange()
    this.addonFeature = this.auth.getAddonFeatureLists();

    this.getLanguageData();
    this.module = this.perm.capable('manage_modules', 'read');

    var profileInfo = JSON.parse(this.auth.getProfileInfo());
    this.findMenuPermissions(profileInfo.tenantCategories);
    this.getAccessTableColumnSetups();
  }

  /******************************************** Load Default Table template ******************************************/
  public getAccessTableColumnSetups() {
    this.dynamicTableColumnsService.getProfileTableColumnSetups(this.profileId).subscribe((res: any) => {
      // ✅ FIXED: build complete data structure first, then assign in one shot
      //           Previously mutated this.profileTableColumnData inside forEach
      //           p-table detected partial changes mid-loop → infinite loop
      const data = res;
      data.forEach((element: any) => {
        element.dropdownShow = false;
        element.disabledActionButton = this.checkPermission(element);
        element.selectedTemp = element.template ? element.template.id : null;
        this.radioChange(element);
      });

      // ✅ Assign once after ALL mutations complete — new reference prevents
      //    p-table from detecting mid-loop changes
      this.profileTableColumnData = [...data];
      this.cdr.markForCheck();
    });
  }

  checkPermission(element: any) {
    if (element.apiModule === 'assets') {
      var tenantPackages = this.auth.getPackageCodeWithList('LISTVIEW_ASSETS');
      return this.changeValueBaseOnPackage(tenantPackages.addonPackageList);
    } else if (element.apiModule === 'contract') {
      var tenantPackages = this.auth.getPackageCodeWithList('LISTVIEW_CONTRACT');
      return this.changeValueBaseOnPackage(tenantPackages.addonPackageList);
    } else if (element.apiModule === 'customer') {
      var tenantPackages = this.auth.getPackageCodeWithList('LISTVIEW_CUSTOMER');
      return this.changeValueBaseOnPackage(tenantPackages.addonPackageList);
    } else if (element.apiModule === 'job_order') {
      var tenantPackages = this.auth.getPackageCodeWithList('LISTVIEW_JO');
      return this.changeValueBaseOnPackage(tenantPackages.addonPackageList);
    } else if (element.apiModule === 'maintenance_item') {
      var tenantPackages = this.auth.getPackageCodeWithList('LISTVIEW_PM');
      return this.changeValueBaseOnPackage(tenantPackages.addonPackageList);
    } else if (element.apiModule === 'product') {
      var tenantPackages = this.auth.getPackageCodeWithList('LISTVIEW_SPARE_PARTS');
      return this.changeValueBaseOnPackage(tenantPackages.addonPackageList);
    } else if (element.apiModule === 'project') {
      var tenantPackages = this.auth.getPackageCodeWithList('LISTVIEW_PROJECTS');
      return this.changeValueBaseOnPackage(tenantPackages.addonPackageList);
    } else if (element.apiModule === 'sales_order') {
      var tenantPackages = this.auth.getPackageCodeWithList('LISTVIEW_SALES_ORDERS');
      return this.changeValueBaseOnPackage(tenantPackages.addonPackageList);
    } else if (element.apiModule === 'site') {
      var tenantPackages = this.auth.getPackageCodeWithList('LISTVIEW_SITES');
      return this.changeValueBaseOnPackage(tenantPackages.addonPackageList);
    } else if (element.apiModule === 'task') {
      var tenantPackages = this.auth.getPackageCodeWithList('LISTVIEW_TASKS');
      return this.changeValueBaseOnPackage(tenantPackages.addonPackageList);
    } else if (element.apiModule === 'ticket') {
      var tenantPackages = this.auth.getPackageCodeWithList('LISTVIEW_TICKETS');
      return this.changeValueBaseOnPackage(tenantPackages.addonPackageList);
    } else {
      return false;
    }
  }

  changeValueBaseOnPackage(addonPackageList: any) {
    return addonPackageList == 'none' ? true : false;
  }

  /******************************************** Radio Change ******************************************/
  public radioChange(element: any) {
    if (this.profile.profile.defaultTenantCategory === "SALES") {
      this.profile.profile.sales = true;
    } else if (this.profile.profile.defaultTenantCategory === "SERVICE") {
      this.profile.profile.service = true;
    } else if (this.profile.profile.defaultTenantCategory === "FLEET") {
      this.profile.profile.fleet = true;
    }
    this.chkChange(element);
  }

  public chkChange(element: any) {
    // ✅ REMOVED: this.addonFeature = this.auth.getAddonFeatureLists()
    //             was returning a new object on every call — causing infinite
    //             change detection loop with p-table. Now uses this.addonFeature
    //             which is set once in ngOnInit().
    element.moduleAccessShow = false;

    for (var i = 0; i <= this.profile.moduleAccessFeatures.length - 1; i++) {
      if (this.profile.moduleAccessFeatures[i].feature.internalName === "customers" && element.module == "Customer" && this.profile.moduleAccessFeatures[i].readFeature) {
        element.url = '/customer/listView';
        element.moduleAccessShow = this.auth.getAddonPackageListsToHide('LISTVIEW_CUSTOMER') ? true : false;
      } else if (element.apiModule === "sales_order" && element.module == "Sales Order" && this.profile.moduleAccessFeatures[i].readFeature) {
        element.moduleAccessShow = this.auth.getAddonPackageListsToHide('LISTVIEW_SALES_ORDERS') ? true : false;
        element.url = '/salesOrder/listView';
      } else if (this.profile.moduleAccessFeatures[i].feature.internalName === "job_order" && element.module == "Job Order" && this.profile.moduleAccessFeatures[i].readFeature) {
        element.moduleAccessShow = this.auth.getAddonPackageListsToHide('LISTVIEW_JO') ? true : false;
        element.url = '/jobOrder/listView';
      } else if (this.profile.moduleAccessFeatures[i].feature.internalName === "products" && element.module == "Product" && this.profile.moduleAccessFeatures[i].readFeature) {
        element.moduleAccessShow = this.auth.getAddonPackageListsToHide('LISTVIEW_SPARE_PARTS') ? true : false;
        element.url = '/products/listView';
      } else if (this.profile.moduleAccessFeatures[i].feature.internalName === "tasks" && element.module == "Task" && this.profile.moduleAccessFeatures[i].readFeature) {
        element.moduleAccessShow = this.auth.getAddonPackageListsToHide('LISTVIEW_TASKS') ? true : false;
        element.url = '/tasks/listView';
      } else if (this.profile.moduleAccessFeatures[i].feature.internalName === "assets" && element.module == "Asset" && this.profile.moduleAccessFeatures[i].readFeature) {
        element.moduleAccessShow = this.auth.getAddonPackageListsToHide('LISTVIEW_ASSETS') ? true : false;
        element.url = '/asset/listView';
      } else if (this.profile.moduleAccessFeatures[i].feature.internalName === "assets_contracts" && element.module == "Contract" && this.profile.moduleAccessFeatures[i].readFeature) {
        element.moduleAccessShow = this.tenantPackagesContract.addonPackageList == 'CONTRACT' && this.auth.getAddonPackageListsToHide('LISTVIEW_CONTRACT') ? true : false;
        element.url = '/contracts/listView';
      } else if (this.profile.moduleAccessFeatures[i].feature.internalName === "scheduled_maintenance" && element.module == "Preventive Maintenance" && this.profile.moduleAccessFeatures[i].readFeature) {
        element.moduleAccessShow = this.auth.getAddonPackageListsToHide('PM') ? true : false;
        element.url = '/preventiveMaintenance/listView';
      } else if (this.profile.moduleAccessFeatures[i].feature.internalName === "tickets" && element.module == "Ticket" && this.profile.moduleAccessFeatures[i].readFeature) {
        // ✅ FIXED: uses this.addonFeature (set once in ngOnInit) instead of calling getAddonFeatureLists() here
        element.moduleAccessShow = this.addonFeature.addonSLA == 'SLA' && this.auth.getAddonPackageListsToHide('LISTVIEW_TICKETS') ? true : false;
        element.url = '/ticket/listView';
      } else if (this.profile.moduleAccessFeatures[i].feature.internalName === "site" && element.module == "Site" && this.profile.moduleAccessFeatures[i].readFeature) {
        element.moduleAccessShow = this.tenantPackagesSites.addonPackageList == 'SITE' && this.auth.getAddonPackageListsToHide('LISTVIEW_SITES') ? true : false;
        element.url = '/sites/listView';
      } else if (this.profile.moduleAccessFeatures[i].feature.internalName === "project" && element.module == "Project" && this.profile.moduleAccessFeatures[i].readFeature) {
        element.moduleAccessShow = this.auth.getAddonPackageListsToHide('LISTVIEW_PROJECTS') ? true : false;
        element.url = '/projects/listView';
      }
    }
  }

  editView(index: any) {
    if (this.profileTableColumnData[index].disabledActionButton) { return false; }
    this.profileTableColumnData[index].dropdownShow = !this.profileTableColumnData[index].dropdownShow;
    this.cdr.markForCheck();
  }

  showConfirm(index: any) {
    this.confirmYes = true;
    this.selectedData = this.profileTableColumnData[index];
    this.approveForAll();
  }

  cancel() { this.confirmYes = false; }

  approveForAll() {
    var req = {
      id: this.selectedData.id,
      templateId: this.selectedData.selectedTemp,
      apiModule: this.selectedData.apiModule,
      userId: this.profileId,
    };

    this.dynamicTableColumnsService.saveUsersTableTemplate(req).subscribe(
      (res: any) => {
        this.spinner.hide();
        this.errorUtil.setErrorMessage(200, this.translate?.general.messages.updatedSuccessfully, null, 'success', 1000);
        this.cancel();
        this.getAccessTableColumnSetups();
      },
      (err) => { this.spinner.hide(); }
    );
  }

  redirectPage(element: any, index: any) {
    if (this.profileTableColumnData[index].disabledActionButton) { return false; }
    this.router.navigate(['dashboard/setup/' + element.url]);
  }

  /******************************************** Menu Permission ******************************************/
  private findMenuPermissions(tenantCategories: any) {
    if (tenantCategories.indexOf("SALES") != -1) { this.salePer = true; }
    if (tenantCategories.indexOf("SERVICE") != -1) { this.servicePer = true; }
    if (tenantCategories.indexOf("FLEET") != -1) { this.fleetPer = true; }
  }

  /******************************************** Language ******************************************/
  getLanguageData() {
    this.push(
      this.languageTranslateService.wordSrc$.subscribe((data) => {
        if (Object.keys(data).length != 0) {
          this.translate = data;
          this.cdr.markForCheck();
        }
      })
    );
  }

  push(obs: any) { super.push(obs); }

  ngOnDestroy(): void { super.ngOnDestroy(); }
}